import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Users, Shield, CreditCard, AlertCircle, CheckCircle, Clock, Eye, Building2, Home, DoorOpen, UserCheck, UserX } from "lucide-react";

var API_URL = process.env.REACT_APP_BACKEND_URL;

var allRoles = ["admin", "realestate_admin", "realestate_manager", "viewer"];

var emptyForm = {
  name: "",
  email: "",
  password: "",
  role: "viewer",
};

export default function UsersPage() {
  var { user, token } = useAuth();
  var { t } = useTranslation();
  var [users, setUsers] = useState([]);
  var [loading, setLoading] = useState(true);
  var [dialogOpen, setDialogOpen] = useState(false);
  var [assignDialogOpen, setAssignDialogOpen] = useState(false);
  var [editingUser, setEditingUser] = useState(null);
  var [selectedViewerForAssign, setSelectedViewerForAssign] = useState(null);
  var [form, setForm] = useState(emptyForm);
  var [search, setSearch] = useState("");
  var [availableAssignments, setAvailableAssignments] = useState({ properties: [], units: [] });
  var [selectedProperties, setSelectedProperties] = useState([]);
  var [selectedUnits, setSelectedUnits] = useState([]);
  var [assignmentSearch, setAssignmentSearch] = useState("");
  var [loadingAssignments, setLoadingAssignments] = useState(false);

  // Determine if user can manage users
  var canManageUsers = user?.role === "admin" || user?.role === "realestate_admin" || user?.role === "realestate_manager";
  var isAdmin = user?.role === "admin";

  // Get allowed roles this user can create
  function getAllowedRoles() {
    var userRole = user?.role || "viewer";
    if (userRole === "admin") return allRoles;
    if (userRole === "realestate_admin") return ["realestate_manager", "viewer"];
    if (userRole === "realestate_manager") return ["viewer"];
    return [];
  }

  useEffect(function() {
    if (canManageUsers && token) fetchUsers();
    else if (!canManageUsers) setLoading(false);
  }, [canManageUsers, token]);

  async function fetchUsers() {
    try {
      var response = await fetch(API_URL + "/api/users", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        setUsers(await response.json());
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update user - build update object based on permissions
        var updateData = { name: form.name, role: form.role };
        
        // Add email if changed and allowed
        if (canEditEmailPassword(editingUser) && form.email && form.email !== editingUser.email) {
          updateData.email = form.email;
        }
        
        // Add password if provided and allowed
        if (canEditEmailPassword(editingUser) && form.password) {
          updateData.password = form.password;
        }
        
        var response = await fetch(API_URL + "/api/users/" + editingUser.user_id, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
          credentials: "include",
          body: JSON.stringify(updateData),
        });

        if (response.ok) {
          toast.success(t("common.success"));
          setDialogOpen(false);
          setEditingUser(null);
          setForm(emptyForm);
          fetchUsers();
        } else {
          var error = await response.json();
          toast.error(error.detail || t("common.error"));
        }
      } else {
        // Create new user - use the /api/users endpoint (not register)
        var response = await fetch(API_URL + "/api/users", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
          credentials: "include",
          body: JSON.stringify(form),
        });

        if (response.ok) {
          toast.success(t("common.success"));
          setDialogOpen(false);
          setForm(emptyForm);
          fetchUsers();
        } else {
          var error = await response.json();
          toast.error(error.detail || t("common.error"));
        }
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  function handleEdit(u) {
    setEditingUser(u);
    setForm({
      name: u.name || "",
      email: u.email || "",
      password: "",
      role: u.role || "viewer",
    });
    setDialogOpen(true);
  }

  async function handleDelete(userId) {
    if (!window.confirm(t("common.confirmDelete"))) return;
    try {
      var response = await fetch(API_URL + "/api/users/" + userId, {
        method: "DELETE",
        credentials: "include",
        headers: { Authorization: "Bearer " + token },
      });
      if (response.ok) {
        toast.success(t("common.success"));
        fetchUsers();
      } else {
        var error = await response.json();
        toast.error(error.detail || t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  async function handleToggleActive(targetUser) {
    var currentlyActive = targetUser.is_active !== false;
    var confirmMsg = currentlyActive ? t("users.confirmDeactivate", { name: targetUser.name }) : t("users.confirmActivate", { name: targetUser.name });
    if (!window.confirm(confirmMsg)) return;
    try {
      var response = await fetch(API_URL + "/api/users/" + targetUser.user_id + "/toggle-active", {
        method: "PUT",
        credentials: "include",
        headers: { Authorization: "Bearer " + token },
      });
      if (response.ok) {
        toast.success(currentlyActive ? t("users.deactivateSuccess") : t("users.activateSuccess"));
        fetchUsers();
      } else {
        var error = await response.json();
        toast.error(error.detail || t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  async function openAssignDialog(viewerUser) {
    setSelectedViewerForAssign(viewerUser);
    setSelectedProperties(viewerUser.assigned_properties || []);
    setSelectedUnits(viewerUser.assigned_units || []);
    setAssignmentSearch("");
    setLoadingAssignments(true);
    
    try {
      var response = await fetch(API_URL + "/api/users/" + viewerUser.user_id + "/available-assignments", {
        credentials: "include",
        headers: { Authorization: "Bearer " + token },
      });
      if (response.ok) {
        setAvailableAssignments(await response.json());
      }
    } catch (error) {
      console.error("Error fetching available assignments:", error);
    } finally {
      setLoadingAssignments(false);
    }
    
    setAssignDialogOpen(true);
  }

  async function handleSaveAssignments() {
    if (!selectedViewerForAssign) return;
    
    try {
      var response = await fetch(API_URL + "/api/users/" + selectedViewerForAssign.user_id + "/assignments", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        credentials: "include",
        body: JSON.stringify({
          assigned_properties: selectedProperties,
          assigned_units: selectedUnits
        }),
      });
      
      if (response.ok) {
        toast.success(t("users.assignmentsUpdated"));
        setAssignDialogOpen(false);
        setSelectedViewerForAssign(null);
        setAssignmentSearch("");
        fetchUsers();
      } else {
        var error = await response.json();
        toast.error(error.detail || t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  function getFilteredProperties() {
    var props = availableAssignments.properties || [];
    if (!assignmentSearch) return props;
    var searchLower = assignmentSearch.toLowerCase();
    var filtered = [];
    for (var i = 0; i < props.length; i++) {
      if (props[i].name.toLowerCase().indexOf(searchLower) >= 0) {
        filtered.push(props[i]);
      }
    }
    return filtered;
  }

  function getFilteredUnits() {
    var units = availableAssignments.units || [];
    if (!assignmentSearch) return units;
    var searchLower = assignmentSearch.toLowerCase();
    var filtered = [];
    for (var i = 0; i < units.length; i++) {
      if (units[i].name.toLowerCase().indexOf(searchLower) >= 0) {
        filtered.push(units[i]);
      }
    }
    return filtered;
  }

  function getPropertyNameById(propertyId) {
    var props = availableAssignments.properties || [];
    for (var i = 0; i < props.length; i++) {
      if (props[i].property_id === propertyId) {
        return props[i].name;
      }
    }
    return propertyId;
  }

  function toggleProperty(propertyId) {
    if (selectedProperties.indexOf(propertyId) >= 0) {
      setSelectedProperties(selectedProperties.filter(function(id) { return id !== propertyId; }));
    } else {
      setSelectedProperties([...selectedProperties, propertyId]);
    }
  }

  function toggleUnit(unitId) {
    if (selectedUnits.indexOf(unitId) >= 0) {
      setSelectedUnits(selectedUnits.filter(function(id) { return id !== unitId; }));
    } else {
      setSelectedUnits([...selectedUnits, unitId]);
    }
  }

  function getAssignmentCount(targetUser) {
    var propCount = (targetUser.assigned_properties || []).length;
    var unitCount = (targetUser.assigned_units || []).length;
    return propCount + unitCount;
  }

  function getRoleBadge(role) {
    var roleClasses = {
      admin: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
      realestate_admin: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
      realestate_manager: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
      viewer: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
    };
    return <Badge className={roleClasses[role] || ""}>{t("users.roles." + role)}</Badge>;
  }

  function getFilteredUsers() {
    var result = [];
    for (var i = 0; i < users.length; i++) {
      var u = users[i];
      var nameMatch = u.name && u.name.toLowerCase().indexOf(search.toLowerCase()) >= 0;
      var emailMatch = u.email && u.email.toLowerCase().indexOf(search.toLowerCase()) >= 0;
      if (nameMatch || emailMatch) result.push(u);
    }
    return result;
  }

  function renderRoleOptions() {
    var allowedRoles = getAllowedRoles();
    var items = [];
    for (var i = 0; i < allowedRoles.length; i++) {
      var r = allowedRoles[i];
      items.push(<SelectItem key={r} value={r}>{t("users.roles." + r)}</SelectItem>);
    }
    return items;
  }

  function canEditUser(targetUser) {
    // Users can always edit themselves
    if (targetUser.user_id === user?.user_id) return true;
    // Admin can edit anyone
    if (user?.role === "admin") return true;
    // Others can only edit users they created (parent_user_id matches)
    return targetUser.parent_user_id === user?.user_id;
  }

  function canDeleteUser(targetUser) {
    // Same rules as edit
    return canEditUser(targetUser);
  }

  function canEditEmailPassword(targetUser) {
    // Admin can edit anyone's email/password
    if (user?.role === "admin") return true;
    // Others can only edit their own email/password
    return targetUser.user_id === user?.user_id;
  }

  function getSubscriptionBadge(u) {
    var status = u.subscription_status || "active";
    if (status === "active") {
      return <Badge className="status-active gap-1"><CheckCircle className="w-3 h-3" />{t("subscription.active")}</Badge>;
    }
    if (status === "trial") {
      var trialEnds = u.trial_ends_at ? new Date(u.trial_ends_at).toLocaleDateString() : "";
      return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 gap-1"><Clock className="w-3 h-3" />{t("subscription.trial")} {trialEnds ? "→ " + trialEnds : ""}</Badge>;
    }
    if (status === "past_due") {
      return <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 gap-1"><AlertCircle className="w-3 h-3" />{t("subscription.pastDue")}</Badge>;
    }
    if (status === "expired" || status === "cancelled") {
      return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 gap-1"><AlertCircle className="w-3 h-3" />{t("subscription.expired")}</Badge>;
    }
    return <Badge variant="outline">{status}</Badge>;
  }

  if (!canManageUsers) {
    return (
      <div className="space-y-6" data-testid="users-page">
        <h1 className="text-3xl font-bold tracking-tight">{t("users.title")}</h1>
        <Card className="bento-card">
          <CardContent className="py-12 text-center text-muted-foreground">
            <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t("users.noPermission")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  var filteredUsers = getFilteredUsers();
  var allowedRoles = getAllowedRoles();

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("users.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("users.subtitle")}</p>
        </div>
        {allowedRoles.length > 0 && (
          <Dialog open={dialogOpen} onOpenChange={function(open) { setDialogOpen(open); if (!open) { setEditingUser(null); setForm(emptyForm); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn"><Plus className="w-4 h-4 mr-2" />{t("users.addUser")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingUser ? t("users.editUser") : t("users.addNewUser")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="form-label">{t("common.name")}</Label>
                  <Input value={form.name} onChange={function(e) { setForm({ ...form, name: e.target.value }); }} placeholder="Full name" required data-testid="user-name-input" />
                </div>
                {(!editingUser || (editingUser && canEditEmailPassword(editingUser))) && (
                  <>
                    <div>
                      <Label className="form-label">{t("common.email")}</Label>
                      <Input type="email" value={form.email} onChange={function(e) { setForm({ ...form, email: e.target.value }); }} placeholder="email@example.com" required={!editingUser} data-testid="user-email-input" />
                      {editingUser && <p className="text-xs text-muted-foreground mt-1">{t("users.leaveBlankToKeep")}</p>}
                    </div>
                    <div>
                      <Label className="form-label">{t("common.password")}</Label>
                      <Input type="password" value={form.password} onChange={function(e) { setForm({ ...form, password: e.target.value }); }} placeholder="••••••••" required={!editingUser} data-testid="user-password-input" />
                      {editingUser && <p className="text-xs text-muted-foreground mt-1">{t("users.leaveBlankToKeep")}</p>}
                    </div>
                  </>
                )}
                <div>
                  <Label className="form-label">{t("users.role")}</Label>
                  <Select value={form.role} onValueChange={function(v) { setForm({ ...form, role: v }); }}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{renderRoleOptions()}</SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={function() { setDialogOpen(false); }}>{t("common.cancel")}</Button>
                  <Button type="submit" data-testid="user-submit-btn">{editingUser ? t("common.update") : t("common.create")}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder={t("common.search")} value={search} onChange={function(e) { setSearch(e.target.value); }} className="pl-10" data-testid="user-search-input" />
      </div>

      {filteredUsers.length > 0 ? (
        <Card className="bento-card overflow-hidden">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.name")}</TableHead>
                <TableHead>{t("common.email")}</TableHead>
                <TableHead>{t("users.role")}</TableHead>
                <TableHead>{t("users.reportsTo")}</TableHead>
                {isAdmin && <TableHead>{t("subscription.status")}</TableHead>}
                <TableHead className="w-20">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map(function(u) {
                return (
                  <TableRow key={u.user_id} data-testid={"user-row-" + u.user_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={u.picture} />
                          <AvatarFallback className="bg-primary text-primary-foreground text-xs">{u.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div>
                          <span className="font-medium">{u.name}</span>
                          {isAdmin && u.is_active === false && <Badge variant="destructive" className="ml-2 text-xs">{t("subscription.deactivated")}</Badge>}
                          {u.role === "viewer" && getAssignmentCount(u) > 0 && (
                            <Badge variant="outline" className="ml-2 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
                              <Building2 className="w-3 h-3 mr-1" />
                              {getAssignmentCount(u)} {t("users.assignAccess").toLowerCase()}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{getRoleBadge(u.role)}</TableCell>
                    <TableCell>
                      {u.role === "admin" ? (
                        <span className="text-muted-foreground text-sm">-</span>
                      ) : u.parent_name ? (
                        <div>
                          <div className="font-medium text-sm">{u.parent_name}</div>
                          <div className="text-xs text-muted-foreground">{u.parent_email}</div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        {u.role === "admin" ? (
                          <Badge variant="outline" className="text-xs">{t("subscription.exempt")}</Badge>
                        ) : (
                          <div className="space-y-1">
                            {getSubscriptionBadge(u)}
                            {u.last_payment_date && (
                              <div className="text-xs text-muted-foreground">
                                {t("subscription.lastPayment")}: {new Date(u.last_payment_date).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex gap-1">
                        {u.role === "viewer" && canEditUser(u) && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={function() { openAssignDialog(u); }}
                            title={t("users.assignAccess")}
                            data-testid={"assign-btn-" + u.user_id}
                          >
                            <Building2 className="w-4 h-4 text-blue-600" />
                          </Button>
                        )}
                        {isAdmin && u.user_id !== user?.user_id && u.role !== "admin" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={function() { handleToggleActive(u); }}
                            title={u.is_active !== false ? t("users.deactivateUser") : t("users.activateUser")}
                            data-testid={"toggle-active-btn-" + u.user_id}
                          >
                            {u.is_active !== false ? <UserX className="w-4 h-4 text-orange-500" /> : <UserCheck className="w-4 h-4 text-green-600" />}
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={function() { handleEdit(u); }} disabled={!canEditUser(u)}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={function() { handleDelete(u.user_id); }} disabled={!canDeleteUser(u)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="bento-card">
          <CardContent className="py-12 text-center text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t("users.noUsers")}</p>
          </CardContent>
        </Card>
      )}

      {/* Assignment Dialog for Viewers */}
      <Dialog open={assignDialogOpen} onOpenChange={function(open) { 
        setAssignDialogOpen(open); 
        if (!open) { 
          setSelectedViewerForAssign(null); 
          setAssignmentSearch(""); 
        } 
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="assignment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {t("users.assignAccessTitle")}
            </DialogTitle>
            {selectedViewerForAssign && (
              <DialogDescription>
                {t("users.assigningTo")}: <span className="font-medium">{selectedViewerForAssign.name}</span> ({selectedViewerForAssign.email})
              </DialogDescription>
            )}
          </DialogHeader>

          {loadingAssignments ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input 
                  placeholder={t("users.searchPropertiesUnits")} 
                  value={assignmentSearch} 
                  onChange={function(e) { setAssignmentSearch(e.target.value); }} 
                  className="pl-10"
                  data-testid="assignment-search"
                />
              </div>

              {/* Tabs for Properties and Units */}
              <Tabs defaultValue="properties" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="properties" className="flex items-center gap-2">
                    <Home className="w-4 h-4" />
                    {t("users.properties")} ({selectedProperties.length})
                  </TabsTrigger>
                  <TabsTrigger value="units" className="flex items-center gap-2">
                    <DoorOpen className="w-4 h-4" />
                    {t("users.units")} ({selectedUnits.length})
                  </TabsTrigger>
                </TabsList>

                {/* Properties Tab */}
                <TabsContent value="properties" className="mt-4">
                  <ScrollArea className="h-[300px] rounded-md border p-4">
                    {getFilteredProperties().length > 0 ? (
                      <div className="space-y-2">
                        {getFilteredProperties().map(function(prop) {
                          var isSelected = selectedProperties.indexOf(prop.property_id) >= 0;
                          return (
                            <div 
                              key={prop.property_id}
                              className={"flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors " + (isSelected ? "bg-primary/10 border border-primary/30" : "bg-muted/50 hover:bg-muted")}
                              onClick={function() { toggleProperty(prop.property_id); }}
                              data-testid={"property-checkbox-" + prop.property_id}
                            >
                              <Checkbox 
                                checked={isSelected}
                                onCheckedChange={function() { toggleProperty(prop.property_id); }}
                              />
                              <Home className="w-4 h-4 text-muted-foreground" />
                              <span className="flex-1 font-medium">{prop.name}</span>
                              {isSelected && <CheckCircle className="w-4 h-4 text-primary" />}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                        <Home className="w-12 h-12 mb-2 opacity-50" />
                        <p>{t("users.noPropertiesAvailable")}</p>
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>

                {/* Units Tab */}
                <TabsContent value="units" className="mt-4">
                  <ScrollArea className="h-[300px] rounded-md border p-4">
                    {getFilteredUnits().length > 0 ? (
                      <div className="space-y-2">
                        {getFilteredUnits().map(function(unit) {
                          var isSelected = selectedUnits.indexOf(unit.unit_id) >= 0;
                          var propertyName = getPropertyNameById(unit.property_id);
                          return (
                            <div 
                              key={unit.unit_id}
                              className={"flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors " + (isSelected ? "bg-primary/10 border border-primary/30" : "bg-muted/50 hover:bg-muted")}
                              onClick={function() { toggleUnit(unit.unit_id); }}
                              data-testid={"unit-checkbox-" + unit.unit_id}
                            >
                              <Checkbox 
                                checked={isSelected}
                                onCheckedChange={function() { toggleUnit(unit.unit_id); }}
                              />
                              <DoorOpen className="w-4 h-4 text-muted-foreground" />
                              <div className="flex-1">
                                <span className="font-medium">{unit.name}</span>
                                <p className="text-xs text-muted-foreground">{propertyName}</p>
                              </div>
                              {isSelected && <CheckCircle className="w-4 h-4 text-primary" />}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                        <DoorOpen className="w-12 h-12 mb-2 opacity-50" />
                        <p>{t("users.noUnitsAvailable")}</p>
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
              </Tabs>

              {/* Summary */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="text-sm text-muted-foreground">
                  {t("users.selectedSummary", { 
                    properties: selectedProperties.length, 
                    units: selectedUnits.length 
                  })}
                </div>
                {(selectedProperties.length > 0 || selectedUnits.length > 0) && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={function() { setSelectedProperties([]); setSelectedUnits([]); }}
                  >
                    {t("users.clearAll")}
                  </Button>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={function() { setAssignDialogOpen(false); }}>
                  {t("common.cancel")}
                </Button>
                <Button onClick={handleSaveAssignments} data-testid="save-assignments-btn">
                  {t("users.saveAssignments")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
